import { Flag } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFlags } from '../../api/flags';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { StatusIndicator } from '../../components/StatusIndicator';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';

export function FlagListPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const { data: flags, isLoading, error } = useFlags(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Flags');

	const columns = [
		{ key: 'id', label: 'Flag ID', mono: true },
		{ key: 'name', label: 'Name' },
		{ key: 'type', label: 'Type' },
		{ key: 'status', label: 'Status' },
		{ key: 'rollout', label: 'Rollout %', mono: true },
		{ key: 'created', label: 'Created' },
	];

	return (
		<div>
			<PageHeader
				title="Feature Flags"
				breadcrumbs={breadcrumbs}
				actions={
					isAdmin ? (
						<Button variant="primary" onClick={() => navigate(`/projects/${id}/flags/new`)}>
							New Flag
						</Button>
					) : undefined
				}
			/>

			<div className={shared.content}>
				<Card>
					{error ? (
						<p className={shared.errorMessage}>
							{error instanceof Error ? error.message : 'Failed to load flags'}
						</p>
					) : (
						<Table>
							<Table.Header columns={columns} />
							<Table.Body>
								{isLoading ? (
									<SkeletonLoader rows={4} columns={6} />
								) : !flags || flags.length === 0 ? (
									<tr>
										<td colSpan={6}>
											<EmptyState
												icon={Flag}
												title="No flags yet"
												description="Create your first feature flag to start rolling out features."
												action={
													isAdmin
														? {
																label: 'New Flag',
																onClick: () => navigate(`/projects/${id}/flags/new`),
															}
														: undefined
												}
											/>
										</td>
									</tr>
								) : (
									flags.map((flag) => (
										<Table.Row
											key={flag.id}
											onClick={() => navigate(`/projects/${id}/flags/${flag.id}`)}
										>
											<Table.Cell mono>{flag.id}</Table.Cell>
											<Table.Cell>{flag.name}</Table.Cell>
											<Table.Cell>{flag.type}</Table.Cell>
											<Table.Cell>
												<StatusIndicator status={flag.status} />
											</Table.Cell>
											<Table.Cell mono>{flag.rollout_percentage}%</Table.Cell>
											<Table.Cell mono>{formatRelativeTime(flag.created_at)}</Table.Cell>
										</Table.Row>
									))
								)}
							</Table.Body>
						</Table>
					)}
				</Card>
			</div>
		</div>
	);
}
